import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { TimerService } from '../../services/timer/timer.service';

/**
 * Component that displays a friendly description
 * of the time passed since a given timestamp.
 * If the timestamp is older than an hour, don't subscribe
 * to a timer since the message doesn't change frequently.
 * If the timestamp is more recent than an hour, subscribe
 * to a timer that updates every minute.
 * Uses OnPush change detection strategy and logic
 * for timer subscriptions for performance reasons.
 */
@Component({
  selector: 'ui-time-ago',
  templateUrl: './time-ago.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimeAgoComponent implements OnInit {
  @Input() public missingTimestampLabel: string;
  @Input() public case: 'sentence' | 'lower' = 'lower';
  private _timestamp: string | number;
  private timestamp$: BehaviorSubject<string | number> = new BehaviorSubject(
    null
  );
  public timeAgo$: Observable<string>;
  constructor(private timerService: TimerService) {
    dayjs.extend(relativeTime);
  }

  @Input() public set timestamp(value: string | number) {
    this._timestamp = value;
    this.timestamp$.next(value);
  }

  public get timestamp(): string | number {
    return this._timestamp;
  }

  public ngOnInit(): void {
    this.timeAgo$ = this.timestamp$.pipe(
      switchMap(() =>
        this.isRecent ? this.updateMessageOverTime() : this.singleMessage()
      ),
      map((message) => this.changeCase(message))
    );
  }

  private updateMessageOverTime(): Observable<string> {
    return merge(of(null), this.timerService.minutes$).pipe(
      map(() => this.fromNow),
      take(60)
    );
  }

  private singleMessage(): Observable<string> {
    return of(this.fromNow);
  }

  private changeCase(message: string): string {
    if (message && message.length > 0) {
      if (this.case === 'sentence') {
        return message[0].toUpperCase() + message.slice(1);
      } else {
        return message;
      }
    }
  }

  private get isRecent(): boolean {
    const now = dayjs();
    if (this.timestamp) {
      const date = dayjs(this.timestamp);
      return now.diff(date) / 60000 < 60; // less than 60 mins old
    }
    return false;
  }

  private get fromNow(): string {
    if (this.timestamp) {
      return dayjs(this.timestamp).fromNow();
    }
  }
}
